import React, { useMemo } from "react";
import Card from "components/ui/Card/Card";
import DataTable, {
  DataTableColumn,
  SelectOption,
} from "components/ui/DataTable/DataTable";
import { formatDate } from "utils/dateUtils";
import styles from "./DailyReportPage.module.scss";

type DailyReportRow = {
  id: string;
  pump: string;
  name: string;
  reportingAt: string;
  visited: number;
  newMale: number;
  newFemale: number;
  soldWater: number;
  recharge: number;
  returnCard: number;
  damageCard: number;
  rocketPayment: number;
  rechargeDamage: number;
  cashInHand: number;
  rocketAgent: string;
  zone: string;
  pumpId: string;
};

const zoneOptions: SelectOption[] = [
  { label: "Zone 1", value: "Zone 1" },
  { label: "Zone 2", value: "Zone 2" },
  { label: "Zone 3", value: "Zone 3" },
];

const pumpOptions: SelectOption[] = [
  { label: "Ahmedbag", value: "Ahmedbag" },
  { label: "Alamganj", value: "Alamganj" },
  { label: "Basabo 01", value: "Basabo 01" },
  { label: "Basabo 02", value: "Basabo 02" },
];

const reports: DailyReportRow[] = [
  {
    id: "1",
    pump: "Ahmedbag",
    name: "Jamil Ahmed",
    reportingAt: "2024-03-02",
    visited: 42,
    newMale: 12,
    newFemale: 18,
    soldWater: 1520,
    recharge: 12500,
    returnCard: 3,
    damageCard: 1,
    rocketPayment: 8200,
    rechargeDamage: 200,
    cashInHand: 4600,
    rocketAgent: "Rakib Hasan",
    zone: "Zone 1",
    pumpId: "P-001",
  },
  {
    id: "2",
    pump: "Alamganj",
    name: "Abida Rahman",
    reportingAt: "2024-03-03",
    visited: 38,
    newMale: 14,
    newFemale: 21,
    soldWater: 1280,
    recharge: 9800,
    returnCard: 1,
    damageCard: 0,
    rocketPayment: 5400,
    rechargeDamage: 120,
    cashInHand: 4300,
    rocketAgent: "Shimul Chowdhury",
    zone: "Zone 2",
    pumpId: "P-002",
  },
  {
    id: "3",
    pump: "Basabo 01",
    name: "Rahul Karim",
    reportingAt: "2024-03-04",
    visited: 45,
    newMale: 10,
    newFemale: 16,
    soldWater: 1810,
    recharge: 11200,
    returnCard: 2,
    damageCard: 0,
    rocketPayment: 6000,
    rechargeDamage: 80,
    cashInHand: 5120,
    rocketAgent: "Kamal Uddin",
    zone: "Zone 1",
    pumpId: "P-003",
  },
  {
    id: "4",
    pump: "Basabo 02",
    name: "Water Works Ltd.",
    reportingAt: "2024-03-04",
    visited: 26,
    newMale: 6,
    newFemale: 4,
    soldWater: 920,
    recharge: 6300,
    returnCard: 4,
    damageCard: 2,
    rocketPayment: 3100,
    rechargeDamage: 50,
    cashInHand: 2500,
    rocketAgent: "Sadia Rahman",
    zone: "Zone 2",
    pumpId: "P-004",
  },
  {
    id: "5",
    pump: "Ahmedbag",
    name: "City Council",
    reportingAt: "2024-03-05",
    visited: 32,
    newMale: 8,
    newFemale: 11,
    soldWater: 1340,
    recharge: 7800,
    returnCard: 0,
    damageCard: 0,
    rocketPayment: 4200,
    rechargeDamage: 100,
    cashInHand: 3500,
    rocketAgent: "Mahfuz Alam",
    zone: "Zone 1",
    pumpId: "P-001",
  },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

function DailyReportPage() {
  const columns = useMemo<DataTableColumn<DailyReportRow>[]>(
    () => [
      {
        id: "reportingAt",
        header: "Reporting At",
        accessorKey: "reportingAt",
        filter: {
          type: "date",
          fromPlaceholder: "From date",
          toPlaceholder: "To date",
        },
        cell: (value) => <span>{formatDate(value)}</span>,
      },
      {
        id: "zone",
        header: "Zone",
        accessorKey: "zone",
        filter: {
          type: "select",
          options: zoneOptions,
          placeholder: "All zones",
        },
      },
      {
        id: "pump",
        header: "Pump",
        accessorKey: "pump",
        filter: {
          type: "select",
          options: pumpOptions,
          placeholder: "All pumps",
        },
        enableGlobalFilter: true,
      },
      {
        id: "name",
        header: "Name",
        accessorKey: "name",
        filter: { type: "text", placeholder: "User name" },
        enableGlobalFilter: true,
      },
      {
        id: "visited",
        header: "Visited",
        accessorKey: "visited",
        align: "center",
      },
      {
        id: "newTotal",
        header: "New",
        accessor: (row) => row.newMale + row.newFemale,
        cell: (value) => <span>{Number(value ?? 0)}</span>,
        align: "center",
      },
      {
        id: "newMale",
        header: "New Male",
        accessorKey: "newMale",
        align: "center",
      },
      {
        id: "newFemale",
        header: "New Female",
        accessorKey: "newFemale",
        align: "center",
      },
      {
        id: "soldWater",
        header: "Sold Water (Ltr)",
        accessorKey: "soldWater",
        cell: (value) => (
          <span>{formatCurrency(Number(value ?? 0))}</span>
        ),
        align: "right",
      },
      {
        id: "recharge",
        header: "Recharge",
        accessorKey: "recharge",
        cell: (value) => (
          <span>{formatCurrency(Number(value ?? 0))}</span>
        ),
        align: "right",
      },
      {
        id: "returnCard",
        header: "Return Card",
        accessorKey: "returnCard",
        align: "center",
      },
      {
        id: "damageCard",
        header: "Damage Card",
        accessorKey: "damageCard",
        align: "center",
      },
      {
        id: "rocketPayment",
        header: "Rocket Payment",
        accessorKey: "rocketPayment",
        cell: (value) => (
          <span>{formatCurrency(Number(value ?? 0))}</span>
        ),
        align: "right",
      },
      {
        id: "rechargeDamage",
        header: "Recharge Damage",
        accessorKey: "rechargeDamage",
        cell: (value) => (
          <span>{formatCurrency(Number(value ?? 0))}</span>
        ),
        align: "right",
      },
      {
        id: "cashInHand",
        header: "Cash In Hand",
        accessorKey: "cashInHand",
        cell: (value) => (
          <span>{formatCurrency(Number(value ?? 0))}</span>
        ),
        align: "right",
      },
      {
        id: "rocketAgent",
        header: "Rocket Agent",
        accessorKey: "rocketAgent",
        enableGlobalFilter: true,
      },
    ],
    []
  );

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Daily Report</h1>
            <p className={styles.subtitle}>
              Review pump performance, card sales, recharges and cash
              collection across the network.
            </p>
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.secondaryButton}>
              Export CSV
            </button>
            <button type="button" className={styles.primaryButton}>
              Create Report
            </button>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={reports}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={5}
          globalSearchPlaceholder="Search by pump, name, or agent"
          emptyState="No report data matches your filters."
        />
      </Card>
    </div>
  );
}

export default DailyReportPage;
